import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Grid,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/useUser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchLLMGroups } from "../dataaccess/fetchLLMGroups";
import {
  SelectElement,
  SliderElement,
  TextareaAutosizeElement,
  TextFieldElement,
  useForm,
} from "react-hook-form-mui";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateLLMProjectRouting } from "../dataaccess/updateProjectRouting";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { API, graphqlOperation } from "aws-amplify";
import { GraphQLResult } from "@aws-amplify/api";
import { getLLMProject } from "../graphql/queries";
import { GetLLMProjectQuery } from "../API";

const schema = z.object({
  piiRedactionScope: z.string().optional().nullable(),
  piiScope: z.string().optional().nullable(),
  piiGroupId: z.string().optional().nullable(),
  piiMessage: z.string().optional().nullable(),
  promptBlacklist: z.string().optional().nullable(),
  blacklistGroupId: z.string().optional().nullable(),
  blacklistMessage: z.string().optional().nullable(),
  safety: z.number().optional().nullable(),
  safetyGroupId: z.string().optional().nullable(),
  safetyMessage: z.string().optional().nullable(),
  strongModelGroupId: z.string().optional().nullable(),
  routeModel: z.string().optional().nullable(),
});

type RouterFormType = z.infer<typeof schema>;

const Router: React.FC = () => {
  const navigate = useNavigate();
  const { project, entitlements, trialDaysRemaining } = useUser();
  const { logError } = useErrorLogging();

  const pii = [
    {
      key: "ADDRESS",
      group: "place",
    },
    {
      key: "AGE",
      group: "age",
    },
    {
      key: "AWS_ACCESS_KEY",
      group: "credential",
    },
    {
      key: "AWS_SECRET_KEY",
      group: "credential",
    },
    {
      key: "CREDIT_DEBIT_CVV",
      group: "financial",
    },
    {
      key: "CREDIT_DEBIT_EXPIRY",
      group: "financial",
    },
    {
      key: "CREDIT_DEBIT_NUMBER",
      group: "financial",
    },
    {
      key: "DATE_TIME",
      group: "date",
    },
    {
      key: "DRIVER_ID",
      group: "identity",
    },
    {
      key: "EMAIL",
      group: "email",
    },
    {
      key: "INTERNATIONAL_BANK_ACCOUNT_NUMBER",
      group: "financial",
    },
    {
      key: "IP_ADDRESS",
      group: "webaddress",
    },
    {
      key: "LICENSE_PLATE",
      group: "identity",
    },
    {
      key: "MAC_ADDRESS",
      group: "webaddress",
    },
    {
      key: "NAME",
      group: "name",
    },
    {
      key: "PASSWORD",
      group: "credential",
    },
    {
      key: "PHONE",
      group: "phone",
    },
    {
      key: "PIN",
      group: "credential",
    },
    {
      key: "SWIFT_CODE",
      group: "financial",
    },
    {
      key: "URL",
      group: "webaddress",
    },
    {
      key: "USERNAME",
      group: "identity",
    },
    {
      key: "VEHICLE_IDENTIFICATION_NUMBER",
      group: "identity",
    },
    {
      key: "CA_HEALTH_NUMBER",
      group: "identity",
    },
    {
      key: "CA_SOCIAL_INSURANCE_NUMBER",
      group: "identity",
    },
    {
      key: "IN_AADHAAR",
      group: "identity",
    },
    {
      key: "IN_NREGA",
      group: "identity",
    },
    {
      key: "IN_PERMANENT_ACCOUNT_NUMBER",
      group: "identity",
    },
    {
      key: "IN_VOTER_NUMBER",
      group: "identity",
    },
    {
      key: "UK_NATIONAL_HEALTH_SERVICE_NUMBER",
      group: "identity",
    },
    {
      key: "UK_NATIONAL_INSURANCE_NUMBER",
      group: "identity",
    },
    {
      key: "UK_UNIQUE_TAXPAYER_REFERENCE_NUMBER",
      group: "identity",
    },
    {
      key: "BANK_ACCOUNT_NUMBER",
      group: "financial",
    },
    {
      key: "BANK_ROUTING",
      group: "financial",
    },
    {
      key: "PASSPORT_NUMBER",
      group: "identity",
    },
    {
      key: "US_INDIVIDUAL_TAX_IDENTIFICATION_NUMBER",
      group: "identity",
    },
    {
      key: "SSN",
      group: "identity",
    },
  ];

  const uniqueGroups = Array.from(
    new Set(pii.map((entry) => entry.group))
  ).sort();

  const queryClient = useQueryClient();

  const mutationUpdateProject = useMutation({
    mutationFn: updateLLMProjectRouting,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (error) => {
      logError("Error updating project", error as any);
    },
  });

  const {
    data: projectData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["projects", { id: project?.id }],
    queryFn: () =>
      API.graphql(
        graphqlOperation(getLLMProject, { id: project?.id })
      ) as Promise<GraphQLResult<GetLLMProjectQuery>>,
    enabled: !!project?.id,
  });

  const {
    data: groupData,
    isLoading: isLoadingGroups,
    isError: isErrorGroups,
  } = useQuery({
    queryKey: ["groups"],
    queryFn: () => fetchLLMGroups(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  const { handleSubmit, control, reset, watch, setValue, formState } =
    useForm<RouterFormType>({
      values: projectData?.data?.getLLMProject ?? {},
      resolver: zodResolver(schema),
    });

  const piiScopeWatch = watch("piiScope");
  const piiScopeArray = piiScopeWatch?.split("|") ?? [];

  const piiRedactionWatch = watch("piiRedactionScope");
  const piiRedactionArray = piiRedactionWatch?.split("|") ?? [];

  if (isLoading || isLoadingGroups) return <div>Loading...</div>;
  if (isError || isErrorGroups)
    return <div>Error loading project or groups</div>;

  const onSubmit = (data: RouterFormType) => {
    handleUpdate(data);
  };

  const handleUpdate = (values: RouterFormType) => {
    if (!values) return;

    mutationUpdateProject.mutate({
      id: project?.id ?? "",
      piiRedactionScope: piiRedactionArray?.join("|") ?? "",
      piiScope: piiScopeArray?.join("|") ?? "",
      piiGroupId: values.piiGroupId,
      piiMessage: values.piiMessage,
      promptBlacklist: values.promptBlacklist,
      blacklistGroupId: values.blacklistGroupId,
      blacklistMessage: values.blacklistMessage,
      safety: values.safety,
      safetyGroupId: values.safetyGroupId,
      safetyMessage: values.safetyMessage,
      strongModelGroupId: values.strongModelGroupId,
      routeModel: values.routeModel,
    });
  };

  const options = [
    { id: "", label: "Default" },
    ...(groupData?.data?.groupsByProjectId?.items?.map((group) => ({
      id: group?.id ?? "",
      label: group?.name ?? "",
    })) ?? []),
  ];

  return (
    <Box p={2}>
      <Stack spacing={2}>
        <Paper
          elevation={3}
          style={{ borderRadius: 3, border: "1px solid #444" }}
        >
          <Box p={2}>
            <Typography variant="h6">
              Prompt Contains PII Data{" "}
              <Chip
                size="small"
                label="prod / ent"
                title="PII redaction is available at production and enterprise levels"
                color={
                  trialDaysRemaining > 0 ||
                  entitlements?.includes("pii-redaction")
                    ? "default"
                    : "warning"
                }
              />
            </Typography>
            <Typography
              variant="caption"
              color={
                trialDaysRemaining > 0 ||
                entitlements?.includes("pii-redaction")
                  ? "HighlightText"
                  : "InactiveCaptionText"
              }
            >
              If the prompt contains Personally Identifiable Information (PII)
              data. Please select the appropriate group to route the message to
              or a fixed message to send to the caller. For example, route PII
              to an internally hosted model rather than a public model. (English
              and spanish only)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={9}>
                <Typography variant="body2" mt={1}>
                  PII Redaction (token replacement) Scope
                </Typography>
                {uniqueGroups.map((group, index) => (
                  <FormControlLabel
                    key={group}
                    control={
                      <Checkbox
                        onClick={() => {
                          if (piiRedactionArray?.includes(group)) {
                            const newScope = piiRedactionArray
                              ?.filter((item) => item !== group)
                              .join("|");
                            setValue("piiRedactionScope", newScope);
                          } else {
                            const newScope = piiRedactionArray
                              ?.concat(group)
                              .join("|");
                            setValue("piiRedactionScope", newScope);
                          }
                        }}
                        checked={piiRedactionArray?.includes(group) || false}
                      />
                    }
                    label={group}
                    disabled={
                      !(
                        trialDaysRemaining > 0 ||
                        entitlements?.includes("pii-redaction")
                      )
                    }
                  />
                ))}
              </Grid>
              <Grid item xs={12} lg={3}></Grid>
              <Grid item xs={12} lg={9}>
                <Typography variant="body2">
                  PII Blocking / Routing Scope
                </Typography>
                {uniqueGroups.map((group, index) => (
                  <FormControlLabel
                    key={group}
                    control={
                      <Checkbox
                        onClick={() => {
                          if (piiScopeArray?.includes(group)) {
                            const newScope = piiScopeArray
                              ?.filter((item) => item !== group)
                              .join("|");
                            setValue("piiScope", newScope);
                          } else {
                            const newScope = piiScopeArray
                              ?.concat(group)
                              .join("|");
                            setValue("piiScope", newScope);
                          }
                        }}
                        checked={piiScopeArray?.includes(group) || false}
                      />
                    }
                    label={group}
                    disabled={
                      !(
                        trialDaysRemaining > 0 ||
                        entitlements?.includes("pii-redaction")
                      )
                    }
                  />
                ))}
                <br />
                <Button
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                  sx={{ mt: 2 }}
                  disabled={
                    !formState.isDirty ||
                    !(
                      trialDaysRemaining > 0 ||
                      entitlements?.includes("pii-redaction")
                    )
                  }
                >
                  Save
                </Button>
              </Grid>
              <Grid item xs={12} lg={3}>
                <TextFieldElement
                  sx={{ mt: 1 }}
                  name="piiMessage"
                  label="Return Fixed Message"
                  control={control}
                  size="small"
                  fullWidth
                  disabled={
                    !(
                      trialDaysRemaining > 0 ||
                      entitlements?.includes("pii-redaction")
                    )
                  }
                />{" "}
                {project?.automaticModelManagement !== true && (
                  <>
                    {" "}
                    <Typography variant="caption">
                      - OR if no message -
                    </Typography>
                    <SelectElement
                      sx={{ mt: 1 }}
                      name="piiGroupId"
                      label="Service Group"
                      control={control}
                      size="small"
                      fullWidth
                      options={options}
                      disabled={
                        !(
                          trialDaysRemaining > 0 ||
                          entitlements?.includes("pii-redaction")
                        )
                      }
                    />
                  </>
                )}
                {project?.automaticModelManagement === true && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: "block", fontStyle: "italic" }}
                  >
                    Note: In advanced model mode you can also route to a
                    specific group.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Paper
          elevation={3}
          style={{ borderRadius: 3, border: "1px solid #444" }}
        >
          <Box p={2}>
            <Typography variant="h6">
              Prompt Contains Blacklist Words/Topics
            </Typography>
            <Typography variant="caption">
              If the prompt contains certain words or phrases, route to a
              specific service group or return a fixed message to the user. For
              example, if the prompt contains a sensitive project code name or
              product name, return a fixed message or route to an internally
              hosted service.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={9}>
                <TextareaAutosizeElement
                  sx={{ mt: 1 }}
                  control={control}
                  name="promptBlacklist"
                  label="Blacklist words or topics (pipe '|' or new-line separated list)"
                  size="small"
                  fullWidth
                  rows={4}
                  //disabled={vendorValue === "chaos" || vendorValue === "amazon"}
                />

                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={handleSubmit(onSubmit)}
                  disabled={!formState.isDirty}
                >
                  Save
                </Button>
              </Grid>
              <Grid item xs={12} lg={3}>
                <TextFieldElement
                  sx={{ mt: 1 }}
                  name="blacklistMessage"
                  label="Return Fixed Message"
                  control={control}
                  size="small"
                  fullWidth
                />{" "}
                {project?.automaticModelManagement !== true && (
                  <>
                    <Typography variant="caption">
                      - OR if no message -
                    </Typography>
                    <SelectElement
                      sx={{ mt: 1 }}
                      name="blacklistGroupId"
                      label="Service Group"
                      control={control}
                      size="small"
                      fullWidth
                      options={options}
                    />
                  </>
                )}
                {project?.automaticModelManagement === true && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: "block", fontStyle: "italic" }}
                  >
                    Note: In advanced model mode you can also route to a
                    specific group.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Paper
          elevation={3}
          style={{ borderRadius: 3, border: "1px solid #444" }}
        >
          <Box p={2}>
            <Typography variant="h6">
              Harmful Content (Toxicity){" "}
              <Chip
                size="small"
                label="prod / ent"
                title="Toxicity detection is available at production and enterprise levels"
                color={
                  trialDaysRemaining > 0 ||
                  entitlements?.includes("toxicity-detection")
                    ? "default"
                    : "warning"
                }
              />
            </Typography>
            <Typography
              variant="caption"
              color={
                trialDaysRemaining > 0 ||
                entitlements?.includes("toxicity-detection")
                  ? "HighlightText"
                  : "InactiveCaptionText"
              }
            >
              If the prompt is flagged as potentially unsafe, route to a
              specific service group or return a fixed message to the user. For
              example, if the prompt contains hate speech or violence, return a
              fixed message or route to specific service group.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={9}>
                <Box display="flex" justifyContent="center" width="100%">
                  <Box width="75%" m={2}>
                    <SliderElement
                      control={control}
                      name="safety"
                      label="Safety Threshold (0 = off, 0.85 is default)"
                      size="small"
                      min={0}
                      max={1}
                      step={0.05}
                      valueLabelDisplay="on"
                      disabled={
                        !(
                          trialDaysRemaining > 0 ||
                          entitlements?.includes("toxicity-detection")
                        )
                      }
                    />
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={handleSubmit(onSubmit)}
                  disabled={
                    !formState.isDirty ||
                    !(
                      trialDaysRemaining > 0 ||
                      entitlements?.includes("toxicity-detection")
                    )
                  }
                >
                  Save
                </Button>
              </Grid>
              <Grid item xs={12} lg={3}>
                <TextFieldElement
                  sx={{ mt: 1 }}
                  name="safetyMessage"
                  label="Return Fixed Message"
                  control={control}
                  size="small"
                  fullWidth
                  disabled={
                    !(
                      trialDaysRemaining > 0 ||
                      entitlements?.includes("toxicity-detection")
                    )
                  }
                />{" "}
                {project?.automaticModelManagement !== true && (
                  <>
                    <Typography variant="caption">
                      - OR if no message -
                    </Typography>
                    <SelectElement
                      sx={{ mt: 1 }}
                      name="safetyGroupId"
                      label="Service Group"
                      control={control}
                      size="small"
                      fullWidth
                      options={options}
                      disabled={
                        !(
                          trialDaysRemaining > 0 ||
                          entitlements?.includes("toxicity-detection")
                        )
                      }
                    />
                  </>
                )}
                {project?.automaticModelManagement === true && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: "block", fontStyle: "italic" }}
                  >
                    Note: In advanced model mode you can also route to a
                    specific group.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Paper
          elevation={3}
          style={{ borderRadius: 3, border: "1px solid #444" }}
        >
          <Box p={2}>
            <Typography variant="h6">
              Prompt Cost/Quality Routing (ML model){" "}
              <Chip
                size="small"
                label="prod / ent"
                title="Prompt complexity routing is available at production and enterprise levels"
                color={
                  trialDaysRemaining > 0 ||
                  entitlements?.includes("complexity-routing")
                    ? "default"
                    : "warning"
                }
              />
            </Typography>
            <Typography
              variant="caption"
              color={
                trialDaysRemaining > 0 ||
                entitlements?.includes("complexity-routing")
                  ? "HighlightText"
                  : "InactiveCaptionText"
              }
            >
              Route prompts to a lower or higher cost service group based on a
              ML model prediction. Default is to use our pretrained model, but
              you can also train your own prompts and responses.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={9}>
                <br />
                <SelectElement
                  name="routeModel"
                  label="Model for Service Group Prediction"
                  control={control}
                  size="small"
                  fullWidth
                  disabled={
                    !(
                      trialDaysRemaining > 0 ||
                      entitlements?.includes("complexity-routing")
                    )
                  }
                  options={[
                    { id: "none", label: "Off" },
                    {
                      id: "default",
                      label:
                        "Default ML Model (pre-trained on a wide variety of prompts)",
                    },
                    {
                      id: "custom",
                      label:
                        "Custom ML model trained on your data (coming soon)",
                      disabled: true,
                    },
                  ]}
                />

                <Button
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={handleSubmit(onSubmit)}
                  disabled={
                    !formState.isDirty ||
                    !(
                      trialDaysRemaining > 0 ||
                      entitlements?.includes("complexity-routing")
                    )
                  }
                >
                  Save
                </Button>
              </Grid>{" "}
              <Grid item xs={12} lg={3}>
                {" "}
                <br />
                {project?.automaticModelManagement !== true && (
                  <SelectElement
                    name="strongModelGroupId"
                    label="Premium Model Service Group (necessary for complex prompts)"
                    control={control}
                    size="small"
                    fullWidth
                    options={options}
                    disabled={
                      !(
                        trialDaysRemaining > 0 ||
                        entitlements?.includes("complexity-routing")
                      )
                    }
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
};

export default Router;
